import type { EntityCallbacks } from "@firecms/core";

// Deletes uploads that a save or delete leaves unreferenced. FireCMS never
// removes files from the bucket: replacing or clearing a photo only repoints the
// document. Bundled paths and external links are left alone.

// `storeUrl: true` stores the full download URL, which deleteFile accepts as-is.
const isOurUpload = (value: unknown): value is string =>
  typeof value === "string" &&
  (value.startsWith("https://firebasestorage.googleapis.com/") || value.startsWith("gs://"));

// Walks the whole document, since photos sit at different depths per collection.
function collectUploads(value: unknown, found = new Set<string>()): Set<string> {
  if (isOurUpload(value)) found.add(value);
  else if (Array.isArray(value)) value.forEach((item) => collectUploads(item, found));
  else if (value && typeof value === "object")
    Object.values(value).forEach((item) => collectUploads(item, found));
  return found;
}

export const cleanUpReplacedUploads: EntityCallbacks = {
  // Runs after the save so a failed save keeps the old file.
  onSaveSuccess({ values, previousValues, context }) {
    if (!previousValues) return;

    // A photo still used in another slot is kept.
    const stillUsed = collectUploads(values);
    const orphaned = [...collectUploads(previousValues)].filter((url) => !stillUsed.has(url));

    // Not awaited: a failed cleanup should not fail the save.
    orphaned.forEach((url) => {
      context.storageSource
        .deleteFile(url)
        .catch((e) => console.warn(`Could not delete the replaced upload ${url}`, e));
    });
  },

  // Deleting an entry orphans its photos too.
  onDelete({ entity, context }) {
    collectUploads(entity.values).forEach((url) => {
      context.storageSource
        .deleteFile(url)
        .catch((e) => console.warn(`Could not delete the upload ${url}`, e));
    });
  },
};
